import React, { useState, useEffect, useRef } from 'react';
import axios from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Package, 
  Download,
  DollarSign,
  MapPin,
  CheckCircle2,
  Calendar,
  RefreshCw,
  TrendingDown
} from 'lucide-react';
import { toast } from 'sonner';
import { MONTHS } from '../constants';

const Estadisticas = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [ensayos, setEnsayos] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [availableYears, setAvailableYears] = useState([]); // Store available years separately

  // Check if user should have restricted access
  const isRestrictedUser = user && !user.is_superuser && user.assigned_destinations?.length === 1;
  const restrictedDestination = isRestrictedUser ? user.assigned_destinations[0] : null;

  const [filters, setFilters] = useState({
    client: 'all',
    ensayo: 'all',
    destination: 'all',
    year: 'all',
    month: 'all',
    start_date: '',
    end_date: '',
  });

  // Format number with thousand separator (punto) - Spanish format
  const formatPrice = (price) => {
    if (!price && price !== 0) return '';
    
    // Handle string inputs - remove any existing formatting
    let num = price;
    if (typeof price === 'string') {
      // Remove dots (thousand separators) and replace comma with dot for parsing
      num = parseFloat(price.replace(/\./g, '').replace(',', '.'));
    }
    
    if (isNaN(num)) return '';
    
    // Format with Spanish locale: punto for thousands, coma for decimals
    // Use useGrouping: true to ensure thousand separators are always applied
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    }).format(num);
  };

  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchStatistics(isInitialLoadRef.current);
      isInitialLoadRef.current = false;
    }
  }, [filters, loading]);

  const fetchData = async () => {
    try {
      const [clientsRes, ensayosRes, destinationsRes] = await Promise.all([
        axios.get('/clientes'),
        axios.get('/ensayos'),
        axios.get('/destinations'),
      ]);

      // Filter clients by user's assigned destinations for non-superusers
      if (user && !user.is_superuser && user.assigned_destinations?.length > 0) {
        const userDests = user.assigned_destinations;
        const filteredClients = clientsRes.data.filter(client =>
          client.destinations?.some(d => userDests.includes(d))
        );
        setClients(filteredClients);
      } else {
        setClients(clientsRes.data);
      }
      setEnsayos(ensayosRes.data);
      setDestinations(destinationsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
      setLoading(false);
    }
  };

  const fetchStatistics = async (isInitialLoad = false) => {
    try {
      const params = new URLSearchParams();
      if (filters.client !== 'all') params.append('client', filters.client);
      if (filters.ensayo !== 'all') params.append('ensayo', filters.ensayo);
      
      // For restricted users, always use their assigned destination
      if (isRestrictedUser) {
        params.append('destination', restrictedDestination);
      } else if (filters.destination !== 'all') {
        params.append('destination', filters.destination);
      }
      
      if (filters.year !== 'all') params.append('year', filters.year);
      if (filters.month !== 'all') params.append('month', filters.month);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(`/statistics?${params.toString()}`);
      setStats(response.data);
      setLastUpdate(new Date());
      
      // Store available years only on initial load (when no filters applied)
      if (isInitialLoad && response.data?.monthly_trends) {
        const years = new Set();
        Object.keys(response.data.monthly_trends).forEach(key => {
          const year = key.split('-')[0];
          years.add(year);
        });
        setAvailableYears(Array.from(years).sort((a, b) => b.localeCompare(a)));
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Error al cargar estadísticas');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.client !== 'all') params.append('client', filters.client);
      if (filters.ensayo !== 'all') params.append('ensayo', filters.ensayo);
      
      // For restricted users, always use their assigned destination
      if (isRestrictedUser) {
        params.append('destination', restrictedDestination);
      } else if (filters.destination !== 'all') {
        params.append('destination', filters.destination);
      }
      
      if (filters.year !== 'all') params.append('year', filters.year);
      if (filters.month !== 'all') params.append('month', filters.month);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(`/statistics/export?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `estadisticas_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Reporte exportado correctamente');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar el reporte');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      client: 'all',
      ensayo: 'all',
      subcategory: 'all',
      year: 'all',
      month: 'all',
      start_date: '',
      end_date: '',
    });
  };

  const getTop5 = (data) => {
    return Object.entries(data)
      .sort((a, b) => b[1].total_price - a[1].total_price)
      .slice(0, 5);
  };

  const getMaxValue = (data) => {
    if (!data || Object.keys(data).length === 0) return 0;
    return Math.max(...Object.values(data).map(d => d.total_price));
  };

  const formatTimeSince = (date) => {
    if (!date) return '';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'hace unos segundos';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `hace ${minutes} minuto${minutes > 1 ? 's' : ''}`;
    const hours = Math.floor(minutes / 60);
    return `hace ${hours} hora${hours > 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-gray-600 flex items-center gap-2">
          <RefreshCw className="animate-spin" />
          Cargando estadísticas...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="estadisticas-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Estadísticas</h1>
          <p className="text-gray-600 mt-1">
            Análisis y reportes de los trabajos realizados
          </p>
          {lastUpdate && (
            <p className="text-xs text-gray-500 mt-1">
              Actualizado {formatTimeSince(lastUpdate)}
            </p>
          )}
        </div>
        <Button
          onClick={handleExport}
          disabled={exporting || !stats}
          className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
        >
          <Download className="mr-2 h-4 w-4" />
          {exporting ? 'Exportando...' : 'Exportar a Excel'}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Filtros de Análisis</span>
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Limpiar Filtros
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Date Range Filters */}
          <div className="border-b pb-4">
            <Label className="text-sm font-semibold mb-3 block flex items-center gap-2">
              <Calendar size={16} />
              Filtros de Fecha
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Año</Label>
                <Select
                  value={filters.year}
                  onValueChange={(value) => setFilters({ ...filters, year: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los años</SelectItem>
                    {availableYears.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Mes</Label>
                <Select
                  value={filters.month}
                  onValueChange={(value) => setFilters({ ...filters, month: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los meses</SelectItem>
                    {MONTHS.map((month) => (
                      <SelectItem key={month.value} value={month.value}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Desde</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Hasta</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Category Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Cliente</Label>
              <Select
                value={filters.client}
                onValueChange={(value) => setFilters({ ...filters, client: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los clientes</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.name}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Ensayo</Label>
              <Select
                value={filters.ensayo}
                onValueChange={(value) =>
                  setFilters({ ...filters, ensayo: value, subcategory: 'all' })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los ensayos</SelectItem>
                  {ensayos.map((ens) => (
                    <SelectItem key={ens.id} value={ens.name}>
                      {ens.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!isRestrictedUser && (
              <div className="space-y-2">
                <Label>Destino</Label>
                <Select
                  value={filters.destination}
                  onValueChange={(value) => setFilters({ ...filters, destination: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los destinos</SelectItem>
                    {destinations.map((dest) => (
                      <SelectItem key={dest.id} value={dest.name}>
                        {dest.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {isRestrictedUser && (
              <div className="space-y-2">
                <Label>Destino (Acceso Restringido)</Label>
                <Input
                  value={restrictedDestination}
                  disabled
                  className="bg-gray-100 cursor-not-allowed"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {stats ? (
        <>
          <div className={`grid grid-cols-1 ${user?.is_superuser ? 'md:grid-cols-3' : 'md:grid-cols-2 max-w-3xl mx-auto'} gap-6`}>
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Total de Muestras</p>
                    <p className="text-4xl font-bold mt-2" data-testid="total-samples">
                      {stats.total_samples}
                    </p>
                  </div>
                  <Package className="h-12 w-12 text-blue-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm font-medium">Precio Total</p>
                    <p className="text-4xl font-bold mt-2" data-testid="total-price">
                      €{formatPrice(stats.total_price)}
                    </p>
                  </div>
                  <TrendingUp className="h-12 w-12 text-green-200" />
                </div>
              </CardContent>
            </Card>

            {user?.is_superuser && (
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm font-medium">Margen Goya</p>
                    <p className="text-4xl font-bold mt-2" data-testid="net-price">
                      €{formatPrice(stats.total_net_price || 0)}
                    </p>
                    <p className="text-purple-200 text-xs mt-1">Total - Coste GOYA (GAIKER/IVAMI)</p>
                    <p className="text-purple-200 text-xs">20% Total ANMAR</p>
                    <p className="text-purple-200 text-xs">100% Regulatory (salvo Coste Goya)</p>
                  </div>
                  <DollarSign className="h-12 w-12 text-purple-200" />
                </div>
              </CardContent>
            </Card>
            )}
          </div>

          {/* Top 5 Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top 5 Clients */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users size={20} />
                  Top 5 Clientes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {getTop5(stats.by_client).length > 0 ? (
                  <div className="space-y-4">
                    {getTop5(stats.by_client).map(([client, data], index) => {
                      const maxValue = getMaxValue(stats.by_client);
                      const percentage = maxValue > 0 ? (data.total_price / maxValue) * 100 : 0;
                      return (
                        <div key={client} className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium flex items-center gap-2">
                              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold">
                                {index + 1}
                              </span>
                              {client}
                            </span>
                            <span className="text-gray-600">{data.count} muestras</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                              <div
                                className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-bold text-green-600 min-w-[80px] text-right">
                              €{formatPrice(data.total_price)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No hay datos disponibles</p>
                )}
              </CardContent>
            </Card>

            {/* Top 5 Ensayos */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 size={20} />
                  Top 5 Ensayos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {getTop5(stats.by_ensayo).length > 0 ? (
                  <div className="space-y-4">
                    {getTop5(stats.by_ensayo).map(([ensayo, data], index) => {
                      const maxValue = getMaxValue(stats.by_ensayo);
                      const percentage = maxValue > 0 ? (data.total_price / maxValue) * 100 : 0;
                      return (
                        <div key={ensayo} className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium flex items-center gap-2">
                              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-600 text-xs font-bold">
                                {index + 1}
                              </span>
                              {ensayo}
                            </span>
                            <span className="text-gray-600">{data.count} muestras</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                              <div
                                className="bg-gradient-to-r from-purple-500 to-purple-600 h-full rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-bold text-green-600 min-w-[80px] text-right">
                              €{formatPrice(data.total_price)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No hay datos disponibles</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* By Destination */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin size={20} />
                Estadísticas por Destino
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(stats.by_destination).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(stats.by_destination)
                    .sort((a, b) => b[1].total_price - a[1].total_price)
                    .map(([destination, data]) => (
                      <div
                        key={destination}
                        className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-gray-200 hover:border-blue-400 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-bold text-lg text-gray-800">{destination}</h4>
                          <MapPin className="text-blue-500" size={20} />
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-gray-600">
                            <span className="font-semibold">{data.count}</span> muestras
                          </p>
                          <p className="text-xl font-bold text-green-600">
                            €{formatPrice(data.total_price)}{user?.is_superuser && <> / <span className="text-purple-600">€{formatPrice(data.net_price || 0)}</span></>}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No hay datos disponibles</p>
              )}
            </CardContent>
          </Card>

          {/* Monthly Trends */}
          {Object.keys(stats.monthly_trends).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp size={20} />
                  Tendencias Mensuales
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats.monthly_trends)
                    .sort((a, b) => b[0].localeCompare(a[0]))
                    .slice(0, 12)
                    .map(([month, data]) => {
                      const [year, monthNum] = month.split('-');
                      const monthName = MONTHS.find(m => m.value === monthNum)?.label || monthNum;
                      const maxTrend = Math.max(...Object.values(stats.monthly_trends).map(d => d.total_price));
                      const percentage = maxTrend > 0 ? (data.total_price / maxTrend) * 100 : 0;
                      
                      return (
                        <div key={month} className="flex items-center gap-4">
                          <div className="min-w-[120px] text-sm font-medium text-gray-700">
                            {monthName} {year}
                          </div>
                          <div className="flex-1 flex items-center gap-3">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                              <div
                                className="bg-gradient-to-r from-green-500 to-emerald-600 h-full rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <div className="min-w-[60px] text-sm text-gray-600">
                              {data.count} muestras
                            </div>
                            <div className="min-w-[100px] text-sm font-bold text-green-600 text-right">
                              €{formatPrice(data.total_price)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium">No hay datos para mostrar</p>
              <p className="text-sm mt-2">Ajusta los filtros o agrega muestras para ver estadísticas</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Estadisticas;
